import { css } from '@emotion/react';
import { RealityCard } from './RealityCard';
import { SectionTitle } from '@/components/general/SectionTitle';
import { getScreen } from '@/styles/media-query';
import { withPrefix } from 'gatsby';

export type RealitySectionProps = {
  className?: string;
};

export const RealitySection: React.FC<RealitySectionProps> = ({
  className,
}) => {
  return (
    <section
      id="reality"
      css={css`
        padding: 80px 0 88px;

        @media ${getScreen('pc')} {
          padding: 120px 0 232px;
        }
      `}
      className={className}
    >
      <SectionTitle
        css={css`
          padding-right: 20px;
          padding-left: 20px;
        `}
        en="REALITY"
        subText={
          <>
            IPO準備を進めていく上で、想定と現実ギャップや、
            <br />
            多くのIPO準備企業で発生している事例を把握し
            <br
              css={css`
                @media ${getScreen('pc')} {
                  display: none;
                }
              `}
            />
            事前に対処することが重要です。
          </>
        }
      >
        <strong>IPO準備の現場</strong>で起きている<strong>実態</strong>とは
      </SectionTitle>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 72px;
          margin-top: 64px;

          @media ${getScreen('pc')} {
            gap: 144px;
            margin-top: 128px;
          }
        `}
      >
        <RealityCard
          index={1}
          head={
            <>
              上場までにかかる
              <br />
              <strong>IPOコンサル費用の実態</strong>
            </>
          }
          comment={
            <>
              実は4年で
              <br />
              1億3000万円！
            </>
          }
          subText={
            <>
              上場経験者などIPOに詳しい人材が社内にいない場合は、コンサルタントに先導してもらわないとIPO準備が進まない状態が多く見受けられます。コンサルタントに依頼した場合は４年間で平均１億3000万円の費用がかかるとされています。
            </>
          }
          spImage={{
            src: withPrefix('/images/top/reality-sp-1.png'),
            width: 307,
            height: 548,
            alt: '実態は、業務量が多いときも少ないときも同じ費用を支払い続けているケースが多い。Uniforceの場合、IPO準備のプロジェクトを計画的に進めることで、常時コンサルティング契約をせずに、必要な時にスポットで依頼することが可能。',
          }}
          pcImage={{
            src: withPrefix('/images/top/reality-pc-1.png'),
            width: 675,
            height: 272,
            alt: '実態は、業務量が多いときも少ないときも同じ費用を支払い続けているケースが多い。Uniforceの場合、IPO準備のプロジェクトを計画的に進めることで、常時コンサルティング契約をせずに、必要な時にスポットで依頼することが可能。',
          }}
        />
        <RealityCard
          index={2}
          head={
            <>
              <strong>上場をゴール</strong>とした
              <br />
              <strong>ガバナンス構築</strong>
              が招くリスク
            </>
          }
          comment={
            <>
              上場時期が
              <br />
              延期する恐れも！
            </>
          }
          subText={
            <>
              現場の実態とは マッチしていない「理想形」をもとにガバナンス構築を行うと、現場の業務との乖離が生まれます。
              <br />
              見直しの際に工数が大幅に増加するリスクや、無駄の多い業務フローが現場を圧迫し隠蔽をしやすくなってしまい、不祥事リスクが高まります。不祥事が増加すると上場遅延のリスクも高まります。
            </>
          }
          spImage={{
            src: withPrefix('/images/top/reality-sp-2.png'),
            width: 307,
            height: 564,
            alt: '実態は、事業部側のフローを考慮しないカバナンスを構築することで、現場との摩擦や軋轢が生まれ、上場後にカバナンスが機能しないことも。Uniforceの場合、ガバナンスの構築から運用まで、事業部を巻き込むことを前提にプロジェクト体制が設計され、実務に伴ったカバナンス構築が可能です。',
          }}
          pcImage={{
            src: withPrefix('/images/top/reality-pc-2.png'),
            width: 675,
            height: 272,
            alt: '実態は、事業部側のフローを考慮しないカバナンスを構築することで、現場との摩擦や軋轢が生まれ、上場後にカバナンスが機能しないことも。Uniforceの場合、ガバナンスの構築から運用まで、事業部を巻き込むことを前提にプロジェクト体制が設計され、実務に伴ったカバナンス構築が可能です。',
          }}
        />
        <RealityCard
          index={3}
          head={
            <>
              <strong>監査難民</strong>の実態
              <br />
              <small
                css={css`
                  font: normal normal bold 16px/24px 'Noto Sans JP', sans-serif;
                  letter-spacing: 0.77px;
                  padding-top: 3px;

                  @media ${getScreen('pc')} {
                    margin-left: 8px;
                  }
                `}
              >
                ーなぜ<strong>N-3期を繰り返す</strong>のか？ー
              </small>
            </>
          }
          comment={
            <>
              監査契約の
              <br />
              遅延は防げる！
            </>
          }
          subText={
            <>
              監査契約がうまくいかない理由にはビジネスモデルなどの要因もありますが、多くは事前の認識不足が原因です。
              <br />
              事前に必要なタスクやスケジュールを知って準備しておくことで、そのリスクを減らすことが可能です。
            </>
          }
          spImage={{
            src: withPrefix('/images/top/reality-sp-3.png'),
            width: 307,
            height: 395,
            alt: '実態は、主要課題の解決に時間がかかったり、ショートレビュー受けるまでに時間や労力を要してしまい、予定していた監査契約のタイミングが、次の期にズレ込んでしまう。Uniforceの場合、主要課題の解決にかかる時間を短縮し事前に準備をしてできる。',
          }}
          pcImage={{
            src: withPrefix('/images/top/reality-pc-3.png'),
            width: 675,
            height: 272,
            alt: '実態は、主要課題の解決に時間がかかったり、ショートレビュー受けるまでに時間や労力を要してしまい、予定していた監査契約のタイミングが、次の期にズレ込んでしまう。Uniforceの場合、主要課題の解決にかかる時間を短縮し遅延リスクを低減できる。',
          }}
        />
        <RealityCard
          index={4}
          head={
            <>
              <strong>約500</strong>のタスクを<strong>約3年間</strong>に詰め込む<strong>リスク</strong>
            </>
          }
          comment={
            <>
              計画的な準備で
              <br />
              リスクを低減！
            </>
          }
          subText={
            <>
              一般的にIPO準備企業がIPO準備にかける期間は3～3.5年間で、N-3期の中頃からの準備を始めるケースが多くみられます。<br />
              上場審査対応や通常業務にも時間を割く必要があり、膨大な量のタスクをこなしていく必要があります。IPO準備における課題の多くは「タスクの詰め込み」によって発生しています。
            </>
          }
          spImage={{
            src: withPrefix('/images/top/reality-sp-4.png'),
            width: 307,
            height: 524,
            alt: 'Uniforceの場合は、計画的なIPO準備で「タスクの詰込み」を解消！ (ショートレビュー後の業務負荷を低減)',
          }}
          pcImage={{
            src: withPrefix('/images/top/reality-pc-4.png'),
            width: 675,
            height: 272,
            alt: 'Uniforceの場合は、計画的なIPO準備で「タスクの詰込み」を解消！ (ショートレビュー後の業務負荷を低減)',
          }}
        />
      </div>
    </section>
  );
};
